import React, {
  useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import { DialogContext } from '../context/DialogContext';
import Button from '../componentsV2/Button/Button';
import Icon from '../componentsV2/Icons/Icon';

/**
 * @param {children} children
 *  This provider is used to open/close dialog modal
 * It uses DialogContext to provide openDialog and closeDialog functions
 * It uses Button component to render actions
 * passing variants in dialog provider uses color prop in Button component: primary, secondary, etc
 * @returns {
 * DialogContext.Provider
 * }
 */
export const DialogsProvider = ({ children }) => {
  // State to open/close dialog modal
  const [openDialogShow, setOpenDialogShow] = useState(false);

  // State to handle custom dialog
  const [alertBody, setAlertBody] = useState({
    show: false,
    message: '',
    actions: [],
  });

  const openDialog = useCallback((dialog) => {
    setAlertBody(dialog);
    setOpenDialogShow(true);
  }, []);

  const closeDialog = useCallback(() => {
    setAlertBody({
      show: false,
      message: '',
      actions: [],
    });
    setOpenDialogShow(false);
  }, []);
  /**
   * Close modal
   */
  const handleClose = () => {
    // If callback available, runs callback
    if (alertBody.onClose) {
      alertBody.onClose();
    }
    closeDialog();
  };
  return (
    <DialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {openDialogShow && (
        <div className="fixed inset-0 bg-modal-background flex justify-center items-center z-100">
          {/* on medium and large screens the modal width is 508 px, on smaller screens - counting proportionally */}
          <div data-testid="confirm-dialog" className="absolute flex flex-col justify-center items-center bg-vamos-true-white rounded-md min-w-1/3 max-w-127 min-w-fit md:w-127 shadow-vamos-drop-shadow px-4 pb-8">
            <div className="h-12.5 w-full">
              <button type="button" onClick={handleClose} className="absolute w-8 h-8 rounded-full hover:bg-vamos-blue-gray justify-items-center outline-none top-2.5 right-3" aria-label="Close">
                <Icon name="close_modal" />
              </button>
            </div>
            <div className="text-lg pt-4 pb-5.5">
              {/* to pass custom message wrap it into array, see examples in SiteDetailsPage ir useDeleteImport components */}
              {alertBody.message && <p className="text-center">{alertBody.message}</p>}
            </div>
            <div className="flex gap-4 flex-col sm:flex-row">
              {alertBody.actions && alertBody.actions.length
                ? alertBody.actions.map((item) => (
                  <Button
                    key={item.label}
                    color={item.variant}
                    className={item.className ? item.className : ''}
                    onClick={item.onClick && item.onClick}
                    label={item.label}
                    size={item.size || 'md'}
                    fontWeight={item.fontWeight || 'font-medium'}
                    dataTestId={item.dataTestId}
                  />
                ))
                : (
                  <Button
                    color={alertBody.variant}
                    className={alertBody.className ? alertBody.className : ''}
                    label={alertBody.label}
                    size={alertBody.size || 'md'}
                    onClick={handleClose}
                  />
                )}
            </div>
          </div>
        </div>
      )}

    </DialogContext.Provider>
  );
};

DialogsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
